/**
 * reCAPTCHA Enterprise — betriebsnotwendige Bot-Abwehr (D27).
 *
 * ### Warum hier keine Einwilligung mehr geprüft wird
 *
 * Bis zum 21.09.2026 lud dieses Modul erst nach einem Häkchen im Consent-Banner. Der Betreiber
 * hat mit D27 entschieden, die Bot-Abwehr als **technisch notwendig** zu führen: Der offene
 * Chat löst bei jedem Start eine kostenpflichtige KI-Verarbeitung aus und ist ohne Bot-Abwehr
 * nicht betreibbar, und die Registrierung ist der einzige Weg in die Plattform. Rechtsgrundlage
 * ist damit Art. 6 Abs. 1 lit. f DSGVO in Verbindung mit § 25 Abs. 2 Nr. 2 TDDDG.
 *
 * ⚠️ **Das ist eine vertretbare, aber nicht die herrschende Auffassung.** Die
 * Datenschutzkonferenz sieht reCAPTCHA überwiegend *nicht* von der Ausnahme gedeckt, weil
 * Google die Daten auch für eigene Zwecke verarbeitet. Die Abwägung, der Gegeneinwand und die
 * Alternative (ein Captcha ohne Drittübermittlung) stehen in D27; die Entscheidung gehört auf
 * denselben Anwaltstisch wie das übrige Vertragswerk.
 *
 * Wer das zurückdreht, dreht **drei** Dinge zurück: dieses Modul, die Kategorie im
 * Consent-Banner und Ziffer 6 der Datenschutzerklärung. Einzeln geändert widersprechen sie
 * sich, ohne dass etwas fehlschlägt.
 *
 * ### Warum der Site-Key ein Parameter ist und keine Umgebungsvariable
 *
 * Die beiden Apps, die dieses Modul benutzen, lesen ihre Konfiguration unterschiedlich:
 * `apps/marketing` (Astro) über `PUBLIC_RECAPTCHA_SITE_KEY`, `apps/web` (Vite) über
 * `VITE_RECAPTCHA_SITE_KEY`. Beide Namen sind Vorgabe des jeweiligen Bundlers, und beide
 * werden zur Bauzeit **statisch ersetzt** — ein `import.meta.env[name]` mit berechnetem
 * Schlüssel liefert deshalb `undefined`, nicht den Wert. Ein geteiltes Modul kann die
 * Variable also gar nicht selbst lesen. Der Key kommt herein, das ist die einzige Lösung,
 * die in beiden Bundlern richtig ist.
 *
 * ### Was dieses Modul garantiert
 *
 * Dass das Skript **nur dort lädt, wo es gebraucht wird** — erst beim ersten `token()`-Aufruf,
 * nicht beim Import. Ein Seitenaufruf allein löst nichts aus; die Marketing-Startseite holt
 * das Skript also nicht, solange niemand den Chat benutzt.
 */

interface RecaptchaEnterprise {
  enterprise: {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
  };
}

declare global {
  interface Window {
    grecaptcha?: RecaptchaEnterprise;
  }
}

export interface RecaptchaClient {
  /** Der konfigurierte Site-Key, oder `''`. Gehört mit in den Rumpf der Anfrage. */
  readonly siteKey: string;
  /** Token für eine Aktion, oder `null`. `null` heißt immer: Der Server entscheidet. */
  token: (action: string) => Promise<string | null>;
}

export function createRecaptchaClient(siteKey: string): RecaptchaClient {
  let loader: Promise<void> | null = null;

  const load = (): Promise<void> => {
    if (siteKey === '') return Promise.resolve();
    if (loader) return loader;

    loader = new Promise<void>((resolve) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
      script.async = true;
      script.onload = () => resolve();
      /**
       * **Kein `reject`.** Ein blockiertes Skript (Adblocker, strenge Erweiterung) darf die
       * Oberfläche nicht anhalten. Die Anfrage geht dann ohne Token heraus, und der Server
       * entscheidet — auf prod mit einer klaren Fehlermeldung, auf staging folgenlos.
       */
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });

    return loader;
  };

  return {
    siteKey,

    token: async (action: string): Promise<string | null> => {
      if (siteKey === '') return null;

      await load();
      const grecaptcha = window.grecaptcha;
      if (!grecaptcha) return null;

      return new Promise<string | null>((resolve) => {
        grecaptcha.enterprise.ready(() => {
          grecaptcha.enterprise
            .execute(siteKey, { action })
            .then(resolve)
            .catch(() => resolve(null));
        });
      });
    },
  };
}
